import ProductsList, { ProductListTypes } from 'components/Blocks/Product/List/ProductsList';
import ProductsSlider from 'components/Blocks/Product/ProductsSlider';
import { Webline } from 'components/Layout/Webline/Webline';
import { mapConfiguratorProductApiData } from 'connectors/products/Products';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { useConfiguratorProductsQueryApi } from 'graphql/generated';
import {
    CONFIGURATOR_LENGTH_API_IDENTIFIER,
    CONFIGURATOR_WIDTH_API_IDENTIFIER,
    SIZES_OF_CELL_IN_CM,
} from 'helpers/configurator/configuratorConstants';
import { useMediaMin } from 'hooks/ui/useMediaMin';
import { useEffect, useMemo } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { ConfiguratorProductType, ExtendedConfiguratorProductApi } from 'types/configurator';
import { ListedProductType } from 'types/product';

export const ConfiguratorProducts: FC = () => {
    const [{ data: flexiSpaceProductsData }] = useConfiguratorProductsQueryApi();
    const { currencyCode } = useDomainConfig();
    const isTabletOrDesktop = useMediaMin('lg');
    const { productsInDrawer, deleteConfiguratorProduct, updateConfiguratorProduct } = usePersistStore();
    const configuratorProducts = useMemo<ListedProductType[]>(() => {
        if (!flexiSpaceProductsData?.flexiSpaceProducts?.length) {
            return [];
        }

        const productsWithSizes: ExtendedConfiguratorProductApi[] = [];

        flexiSpaceProductsData.flexiSpaceProducts.forEach((product) => {
            const productHeightParameter = product.parameters.find(
                ({ apiIdentifier }) => apiIdentifier === CONFIGURATOR_LENGTH_API_IDENTIFIER,
            );
            const productWidthParameter = product.parameters.find(
                ({ apiIdentifier }) => apiIdentifier === CONFIGURATOR_WIDTH_API_IDENTIFIER,
            );

            if (!productWidthParameter || !productHeightParameter) {
                return null;
            }

            const productWidth = productWidthParameter.values[0].text
                ? Math.round(parseFloat(productWidthParameter.values[0].text) / SIZES_OF_CELL_IN_CM.width)
                : 0;
            const productHeight = productHeightParameter.values[0].text
                ? Math.round(parseFloat(productHeightParameter.values[0].text) / SIZES_OF_CELL_IN_CM.length)
                : 0;

            if (productWidth && productHeight) {
                return productsWithSizes.push({ ...product, flexiWidth: productWidth, flexiHeight: productHeight });
            }

            return null;
        });

        return mapConfiguratorProductApiData(productsWithSizes, currencyCode);
    }, [flexiSpaceProductsData?.flexiSpaceProducts?.length]);

    useEffect(() => {
        // Update old data
        if (flexiSpaceProductsData?.flexiSpaceProducts) {
            productsInDrawer.forEach((product: ConfiguratorProductType | Omit<ConfiguratorProductType, 'uuid'>) => {
                const originalProduct = flexiSpaceProductsData.flexiSpaceProducts?.find((flexiProduct) =>
                    product.id.endsWith(flexiProduct.uuid),
                );

                if (!originalProduct) {
                    deleteConfiguratorProduct(product.id);
                } else if (!('uuid' in product)) {
                    updateConfiguratorProduct(
                        {
                            ...product,
                            uuid: originalProduct.uuid,
                            name: originalProduct.fullName,
                            slug: originalProduct.slug,
                            catalogNumber: originalProduct.catalogNumber,
                        },
                        product.id,
                    );
                }
            });
        }
    }, [
        productsInDrawer,
        flexiSpaceProductsData?.flexiSpaceProducts,
        updateConfiguratorProduct,
        deleteConfiguratorProduct,
    ]);

    return (
        <Webline className="mb-10 mt-5 md:mt-20">
            {!!configuratorProducts.length && isTabletOrDesktop !== undefined && (
                <>
                    {isTabletOrDesktop ? (
                        <ProductsList
                            isConfigurator
                            shouldRedirectOnClick
                            gtmListName="configurator"
                            products={configuratorProducts}
                            type={ProductListTypes.Normal}
                        />
                    ) : (
                        <ProductsSlider
                            isConfigurator
                            shouldRedirectOnClick
                            gtmListName="configurator"
                            idSuffix="-configurator"
                            products={configuratorProducts}
                        />
                    )}
                </>
            )}
        </Webline>
    );
};
